import { type XYChartTheme } from "@visx/xychart";

// Use CSS variables to match system theme
const getCssVar = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const getThemeColors = () => ({
  background: getCssVar("--chart-bg-dark") || "#1e293b",
  axis: getCssVar("--chart-axis-dark") || "#475569",
  grid: getCssVar("--chart-grid-dark") || "#334155",
  textPrimary: getCssVar("--chart-text-primary-dark") || "#f8fafc",
  textSecondary: getCssVar("--chart-text-secondary-dark") || "#cbd5e1",
});

const createTheme = (
  colors: ReturnType<typeof getThemeColors>
): XYChartTheme => {
  const baseAxisStyles = {
    stroke: 'none',
    strokeWidth: 0,
  };

  const baseTickStyles = {
    stroke: colors.axis,
    strokeWidth: 1,
  };

  const baseLabelStyles = {
    fill: colors.textPrimary,
    fontSize: 12,
    fontFamily: "monospace",
  };

  const tickLabelStyles = {
    ...baseLabelStyles,
    fill: colors.textSecondary,
    fontSize: 11,
  };

  // Specific styles for Y-axis tick labels (left)
  const yTickLabelStylesLeft = {
    ...tickLabelStyles,
    textAnchor: "end" as const,
    dominantBaseline: "middle" as const,
    dx: "-0.5em",
  };

  // Specific styles for Y-axis tick labels (right)
  const yTickLabelStylesRight = {
    ...tickLabelStyles,
    textAnchor: "start" as const,
    dominantBaseline: "middle" as const,
    dx: "0.25em",
  };

  // Specific styles for X-axis tick labels
  const xTickLabelStyles = {
    ...tickLabelStyles,
    textAnchor: "middle" as const,
    dy: "0.75em",
  };

  // Define axis styles conforming to AxisStyle
  const axisStyleDefaultsX = {
    axisLabel: { ...baseLabelStyles },
    axisLine: { ...baseAxisStyles },
    tickLabel: { ...xTickLabelStyles },
    tickLine: { ...baseTickStyles },
    tickLength: 5,
  };

  const axisStyleDefaultsYLeft = {
    axisLabel: { ...baseLabelStyles },
    axisLine: { ...baseAxisStyles },
    tickLabel: { ...yTickLabelStylesLeft },
    tickLine: { ...baseTickStyles },
    tickLength: 5,
  };

  const axisStyleDefaultsYRight = {
    axisLabel: { ...baseLabelStyles },
    axisLine: { ...baseAxisStyles },
    tickLabel: { ...yTickLabelStylesRight },
    tickLine: { ...baseTickStyles },
    tickLength: 5,
  };

  return {
    backgroundColor: colors.background,
    colors: [], // Let component define colors
    gridStyles: {
      stroke: colors.grid,
      strokeWidth: 1,
    },
    htmlLabel: {
      color: colors.textPrimary,
    },
    svgLabelBig: {
      ...baseLabelStyles,
      fontSize: 14,
      fontWeight: "bold",
    },
    svgLabelSmall: {
      ...tickLabelStyles,
    },
    axisStyles: {
      x: {
        bottom: axisStyleDefaultsX,
        top: axisStyleDefaultsX,
      },
      y: {
        left: axisStyleDefaultsYLeft,
        right: axisStyleDefaultsYRight,
      },
    },
  };
};

export const getDarkTheme = (): XYChartTheme => createTheme(getThemeColors());
